import type { UpdateTask } from 'api/generated'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog'
import { Download } from 'lucide-react'

interface Props {
  task: UpdateTask
  open: boolean
  onOpenChange: (open: boolean) => void
}

const DownloadLink = ({ url, label }: { url: string; label: string }) => (
  <a
    href={url}
    target='_blank'
    rel='noreferrer'
    className='inline-flex items-center gap-1 font-mono text-sm break-all text-primary underline-offset-4 hover:underline'
  >
    <Download size={14} className='shrink-0' />
    {label}
  </a>
)

export function TasksDetailsDialog({ task, open, onOpenChange }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-2xl'>
        <DialogHeader className='text-left'>
          <DialogTitle>Task {task.id}</DialogTitle>
          <DialogDescription>{task.name ?? task.file ?? '-'}</DialogDescription>
        </DialogHeader>

        <div className='space-y-4 text-sm'>
          <section>
            <h3 className='font-medium'>Job</h3>
            {task.job_class ? (
              <>
                <p className='font-mono'>{task.job_class}</p>
                <pre className='mt-1 max-h-48 overflow-auto rounded-md bg-muted p-2 text-xs'>
                  {JSON.stringify(task.job_arguments, null, 2)}
                </pre>
              </>
            ) : (
              <p className='text-muted-foreground'>
                {task.file
                  ? 'Import da file: viene rielaborato da UpdateTaskJob.'
                  : 'Task creato prima del tracciamento dei job: non si può rilanciare.'}
              </p>
            )}
          </section>

          <section className='space-y-1'>
            <h3 className='font-medium'>File</h3>
            {task.file_url && (
              <DownloadLink url={task.file_url} label={task.file ?? 'File'} />
            )}
            {task.parameter_files.map(({ key, url }) =>
              url ? (
                <div key={key}>
                  <DownloadLink url={url} label={key} />
                </div>
              ) : (
                <p key={key} className='font-mono text-muted-foreground'>
                  {key} (S3 non configurato)
                </p>
              )
            )}
            {!task.file_url && task.parameter_files.length === 0 ? (
              <p className='text-muted-foreground'>Nessun file.</p>
            ) : (
              task.parameter_files.length > 0 && (
                <p className='text-xs text-muted-foreground'>
                  I link dei file parametri valgono 1 ora. Se il job ha già
                  cancellato il file, il download risponde 404.
                </p>
              )
            )}
          </section>

          {task.log && (
            <section>
              <h3 className='font-medium'>Log</h3>
              <pre className='mt-1 max-h-64 overflow-auto rounded-md bg-muted p-2 text-xs whitespace-pre-wrap'>
                {task.log}
              </pre>
            </section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
